import { useCallback, useEffect, useState } from "react";
import { Star, BadgeCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";

export const Stars = ({ value = 0, size = "h-3.5 w-3.5", onPick, testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((i) => {
      const fill = Math.max(0, Math.min(1, value - i + 1));
      return (
        <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i)} className="relative" data-testid={onPick ? `star-pick-${i}` : undefined} aria-label={`${i} stars`}>
          <Star className={`${size} asv-muted`} />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}><Star className={`${size} fill-current asv-accent`} /></span>
        </button>
      );
    })}
  </span>
);

export const RatingBadge = ({ x }) => x.rating_count ? <span className="inline-flex items-center gap-1 text-xs font-bold" data-testid="venue-rating"><Star className="h-3.5 w-3.5 fill-current asv-accent" />{x.rating.toFixed(1)} ({x.rating_count})</span> : null;

export function ReviewForm({ order, item, onClose, onDone }) {
  const r = item.review;
  const [stars, setStars] = useState(r?.stars || 0);
  const [comment, setComment] = useState(r?.comment || "");
  const submit = () => (r ? api.put(`/asv/reviews/${r.id}`, { stars, comment }) : api.post("/asv/reviews", { order_id: order.id, pitch_id: item.pitch_id, stars, comment }))
    .then(() => { toast.success("Thanks for your review"); onDone(); }).catch((e) => toast.error(formatErr(e)));
  const del = () => window.confirm("Delete your review?") && api.delete(`/asv/reviews/${r.id}`).then(() => { toast.success("Review deleted"); onDone(); }).catch((e) => toast.error(formatErr(e)));
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end sm:place-items-center" data-testid="review-form">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="asv-rise relative w-full max-w-md space-y-4 rounded-t-3xl p-6 sm:rounded-3xl" style={{ background: "var(--asv-bg)" }}>
        <div className="flex items-center justify-between"><p className="asv-display text-xl font-extrabold">Rate your visit</p><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <p className="asv-muted text-sm">{item.pitch_name} · {order.vendor_name}</p>
        <Stars value={stars} size="h-9 w-9" onPick={setStars} />
        <textarea className="asv-input !h-28 py-2" maxLength={1000} placeholder="Tell other players how it was (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
        <div className="flex gap-2"><button className="asv-btn flex-1" disabled={!stars} onClick={submit} data-testid="review-submit">{r ? "Update review" : "Post review"}</button>
          {r && <button className="asv-btn-ghost text-red-500" onClick={del} data-testid="review-delete">Delete</button>}</div>
      </div>
    </div>
  );
}

const SORTS = [["newest", "Newest"], ["helpful", "Most helpful"], ["highest", "Highest"], ["lowest", "Lowest"]];

export function VenueReviews({ slug, pid }) {
  const [sort, setSort] = useState("newest");
  const [d, setD] = useState(null);
  const load = useCallback((skip = 0) => api.get(`/asv/venues/${slug}/${pid}/reviews`, { params: { sort, skip } })
    .then((r) => setD((p) => (skip && p ? { ...r.data, items: [...p.items, ...r.data.items] } : r.data))), [slug, pid, sort]);
  useEffect(() => { load(0); }, [load]);
  if (!d) return null;
  return (
    <section className="mt-12" data-testid="venue-reviews">
      <h2 className="asv-display text-2xl font-extrabold">Reviews</h2>
      {!d.count ? <p className="asv-muted mt-2 text-sm">No reviews yet. Players who book through All Sports Venues can review after their game.</p> : <>
        <div className="mt-4 grid gap-6 sm:grid-cols-[200px_1fr]">
          <div><p className="asv-display text-5xl font-extrabold" data-testid="reviews-avg">{d.avg.toFixed(1)}</p><Stars value={d.avg} size="h-5 w-5" /><p className="asv-muted text-sm">{d.count} review{d.count > 1 ? "s" : ""}</p></div>
          <div className="space-y-1.5">{Object.entries(d.breakdown).sort((a, b) => b[0] - a[0]).map(([s, n]) => (
            <div key={s} className="flex items-center gap-2 text-xs"><span className="w-3">{s}</span><div className="asv-soft h-2 flex-1 overflow-hidden rounded-full"><div className="asv-accent-bg h-full" style={{ width: `${(n / d.count) * 100}%` }} /></div><span className="asv-muted w-6">{n}</span></div>))}</div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">{SORTS.map(([k, l]) => <button key={k} className="asv-chip" data-on={sort === k} onClick={() => setSort(k)} data-testid={`reviews-sort-${k}`}>{l}</button>)}</div>
        <div className="mt-4 space-y-3">{d.items.map((r) => (
          <div key={r.id} className="asv-card p-5" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2"><Stars value={r.stars} /><span className="text-sm font-bold">{r.player_name}</span><span className="asv-muted text-xs">{fmtDate(r.created_at.slice(0, 10))}</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-bold asv-accent"><BadgeCheck className="h-3.5 w-3.5" />Verified booking</span></div>
            {r.comment && <p className="mt-2 text-sm">{r.comment}</p>}
            {r.reply && <div className="asv-soft mt-3 rounded-2xl p-3 text-sm" data-testid="review-reply"><p className="text-xs font-bold">Reply from {r.reply.by}</p><p className="mt-1">{r.reply.text}</p></div>}
          </div>))}</div>
        {d.items.length < d.count && <button className="asv-btn-ghost mt-4" onClick={() => load(d.items.length)} data-testid="reviews-more">Load more</button>}
      </>}
    </section>
  );
}
